"use client";

import {
	type AIModel,
	DEFAULT_MODEL_CONFIGS,
	type ImageResolution,
	type ModelConfig,
	clampResolutionForPlan,
	getImageGenerationCreditCost,
} from "@saas/ai-image-generator/types";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export function useImageEffectSettings(model: AIModel) {
	const [requestedResolution, setResolution] =
		useState<ImageResolution>("1K");
	const { canRemoveWatermark, loading } = usePurchases();
	const { data: modelConfigs = DEFAULT_MODEL_CONFIGS } = useQuery({
		queryKey: ["modelConfigs"],
		queryFn: async (): Promise<Record<AIModel, ModelConfig>> => {
			const response = await fetch("/api/image-generator/models");
			if (!response.ok) throw new Error("Failed to fetch model configs");
			return (await response.json()).models;
		},
	});
	const isPaid = loading || canRemoveWatermark;
	const resolution = clampResolutionForPlan(
		model,
		requestedResolution,
		isPaid,
		modelConfigs,
	);
	useEffect(() => {
		if (requestedResolution !== resolution) setResolution(resolution);
	}, [requestedResolution, resolution]);
	return {
		resolution,
		setResolution,
		creditCost: getImageGenerationCreditCost(
			model,
			resolution,
			1,
			modelConfigs,
			true,
		),
		lockedResolutions: new Set(
			isPaid
				? []
				: Object.keys(
						modelConfigs[model]?.creditCostByResolution ?? {},
					),
		),
	};
}
